import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function EmergencyCleaningPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-red-600 to-red-800">
        <div className="max-w-[1400px] mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <i className="ri-alarm-warning-line text-white text-lg"></i>
              <span className="text-white text-[14px] font-semibold">24/7 Emergency Service</span>
            </div>
            <h1 className="text-[64px] font-bold text-white font-['Poppins'] mb-6 leading-tight">
              Emergency Cleaning Services
            </h1>
            <p className="text-white/90 text-[20px] max-w-[800px] mx-auto mb-8">
              24/7 rapid response cleaning for urgent situations, failed inspections, or unexpected health department visits.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a 
                href="tel:+15743299050"
                className="px-8 py-4 bg-white text-red-600 rounded-lg text-[16px] font-semibold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 animate-pulse"
              >
                <i className="ri-phone-line text-xl"></i>
                (574) 329-9050
              </a>
              <a 
                href="tel:+15746221711"
                className="px-8 py-4 bg-white/20 border-2 border-white text-white rounded-lg text-[16px] font-semibold hover:bg-white hover:text-red-600 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-phone-line text-xl"></i>
                (574) 622-1711
              </a>
              <a 
                href="mailto:bbbstanley54@gmail.com"
                className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg text-[16px] font-semibold hover:bg-white hover:text-red-600 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-mail-line text-xl"></i>
                Email for Emergency
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-8">
          {/* Overview */}
          <div className="mb-16">
            <h2 className="text-[42px] font-bold text-gray-900 font-['Poppins'] mb-6">
              When You Need Cleaning NOW
            </h2>
            <p className="text-gray-700 text-[17px] leading-relaxed mb-4">
              Kitchen emergencies don't wait for business hours. Whether you've failed a health inspection, received a surprise visit from the health department, or discovered a critical issue that threatens your operating permit, you need immediate professional help.
            </p>
            <p className="text-gray-700 text-[17px] leading-relaxed mb-4">
              Our Emergency Cleaning Service provides rapid response 24/7, including nights, weekends, and holidays. We understand that every hour your kitchen is closed costs you money, and we're equipped to mobilize quickly and work efficiently to get you back in compliance and operating as fast as possible.
            </p>
          </div>

          {/* Emergency Situations */}
          <div className="mb-16 bg-red-50 border-2 border-red-200 rounded-2xl p-10">
            <h2 className="text-[38px] font-bold text-gray-900 font-['Poppins'] mb-8 text-center">
              Common Emergency Situations
            </h2>
            <div className="grid grid-cols-2 gap-6">
              {[
                {
                  icon: 'ri-close-circle-line',
                  title: 'Failed Health Inspection',
                  description: 'You failed your inspection and have 24-48 hours to correct violations before re-inspection',
                  urgency: 'Critical'
                },
                {
                  icon: 'ri-alarm-warning-line',
                  title: 'Surprise Health Department Visit',
                  description: 'Health inspector showed up unannounced and found critical violations requiring immediate attention',
                  urgency: 'Critical'
                },
                {
                  icon: 'ri-fire-line',
                  title: 'Fire Suppression System Failure',
                  description: 'Your fire suppression system failed inspection and you cannot operate until it\'s cleaned and certified',
                  urgency: 'Critical'
                },
                {
                  icon: 'ri-error-warning-line',
                  title: 'Insurance Inspection Failure',
                  description: 'Your insurance company found violations that must be corrected to maintain coverage',
                  urgency: 'High'
                },
                {
                  icon: 'ri-calendar-close-line',
                  title: 'Missed Scheduled Cleaning',
                  description: 'Your regular cleaning was missed and you\'re now overdue for required maintenance',
                  urgency: 'High'
                },
                {
                  icon: 'ri-building-line',
                  title: 'Pre-Sale Property Inspection',
                  description: 'You\'re selling your restaurant and need immediate cleaning for buyer\'s inspection',
                  urgency: 'Medium'
                }
              ].map((item, index) => (
                <div key={index} className="flex gap-4 items-start p-4 bg-white rounded-xl">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-500 text-white rounded-full flex-shrink-0">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-[18px] font-bold text-gray-900">{item.title}</h3>
                      <span className={`px-2 py-1 text-[11px] font-bold rounded-full ${
                        item.urgency === 'Critical' ? 'bg-red-500 text-white' :
                        item.urgency === 'High' ? 'bg-orange-500 text-white' :
                        'bg-yellow-500 text-white'
                      }`}>
                        {item.urgency}
                      </span>
                    </div>
                    <p className="text-gray-700 text-[15px]">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Our Response */}
          <div className="mb-16">
            <h2 className="text-[42px] font-bold text-gray-900 font-['Poppins'] mb-8">
              Our Emergency Response Process
            </h2>
            <div className="space-y-6">
              {[
                {
                  step: '01',
                  title: 'Immediate Phone Assessment',
                  description: 'Call us 24/7 and speak directly with an experienced technician who will assess your situation and provide immediate guidance.',
                  time: '0-5 minutes'
                },
                {
                  step: '02',
                  title: 'Rapid Team Mobilization',
                  description: 'We dispatch our nearest available crew with all necessary equipment to your location immediately.',
                  time: '30-90 minutes'
                },
                {
                  step: '03',
                  title: 'On-Site Evaluation',
                  description: 'Our team arrives, evaluates the situation, and creates an action plan to address all critical issues first.',
                  time: '15-30 minutes'
                },
                {
                  step: '04',
                  title: 'Priority Cleaning Execution',
                  description: 'We work efficiently to clean and correct all violations, focusing on critical items that prevent you from operating.',
                  time: '2-8 hours'
                },
                {
                  step: '05',
                  title: 'Documentation & Certification',
                  description: 'Complete documentation with photos and certification to present to inspectors for re-inspection approval.',
                  time: '30 minutes'
                },
                {
                  step: '06',
                  title: 'Follow-Up Support',
                  description: 'We stay available to answer inspector questions and provide additional support if needed during re-inspection.',
                  time: 'As needed'
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex gap-6 items-start"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-red-600 text-white text-[24px] font-bold rounded-xl flex-shrink-0">
                    {item.step}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-[22px] font-bold text-gray-900">{item.title}</h3>
                      <span className="text-red-600 text-[14px] font-semibold">{item.time}</span>
                    </div>
                    <p className="text-gray-600 text-[16px] leading-relaxed">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="mb-16 bg-gradient-to-br from-red-600 to-red-800 rounded-2xl p-12 text-white">
            <h2 className="text-[42px] font-bold font-['Poppins'] mb-8 text-center">
              Why Choose Our Emergency Service
            </h2>
            <div className="grid grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-time-line',
                  title: '24/7 Availability',
                  description: 'We answer calls and dispatch crews any time, day or night, including holidays'
                },
                {
                  icon: 'ri-speed-line',
                  title: 'Rapid Response',
                  description: 'Average response time of 60 minutes or less in our service area'
                },
                {
                  icon: 'ri-team-line',
                  title: 'Experienced Crews',
                  description: 'Our technicians have handled hundreds of emergency situations'
                },
                {
                  icon: 'ri-tools-line',
                  title: 'Fully Equipped',
                  description: 'All emergency vehicles carry complete equipment for any situation'
                },
                {
                  icon: 'ri-checkbox-circle-line',
                  title: 'High Success Rate',
                  description: '98% of our emergency clients pass their re-inspection on first attempt'
                },
                {
                  icon: 'ri-customer-service-line',
                  title: 'Inspector Support',
                  description: 'We can be present during re-inspection to answer technical questions'
                }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-white/20 rounded-full">
                    <i className={`${item.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-[20px] font-bold mb-2">{item.title}</h3>
                  <p className="text-white/90 text-[15px]">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing */}
          <div className="mb-16">
            <h2 className="text-[42px] font-bold text-gray-900 font-['Poppins'] mb-8 text-center">
              Emergency Service Pricing
            </h2>
            <div className="grid grid-cols-3 gap-6">
              {[
                {
                  time: 'Business Hours',
                  hours: 'Mon-Fri, 8am-5pm',
                  rate: 'Standard Rate',
                  description: 'Regular pricing applies for emergency calls during normal business hours'
                },
                {
                  time: 'After Hours',
                  hours: 'Evenings & Weekends',
                  rate: '+50% Premium',
                  description: 'After-hours premium for nights, weekends, and early mornings'
                },
                {
                  time: 'Holidays',
                  hours: 'Major Holidays',
                  rate: '+100% Premium',
                  description: 'Holiday premium for Thanksgiving, Christmas, New Year\'s, etc.'
                }
              ].map((item, index) => (
                <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center hover:border-red-600 transition-all">
                  <h3 className="text-[24px] font-bold text-gray-900 mb-2">{item.time}</h3>
                  <p className="text-gray-600 text-[14px] mb-4">{item.hours}</p>
                  <div className="text-[32px] font-bold text-red-600 mb-4">{item.rate}</div>
                  <p className="text-gray-700 text-[14px]">{item.description}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-600 text-[15px] mt-6">
              * Final pricing depends on scope of work and location. Call for immediate quote.
            </p>
          </div>

          {/* CTA */}
          <div className="text-center bg-red-50 border-2 border-red-200 rounded-2xl p-12">
            <h2 className="text-[38px] font-bold text-gray-900 font-['Poppins'] mb-4">
              Need Emergency Cleaning Right Now?
            </h2>
            <p className="text-gray-600 text-[17px] mb-8 max-w-[700px] mx-auto">
              Don't wait—every minute counts in an emergency. Call us now for immediate assistance and rapid response.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a 
                href="tel:+15743299050"
                className="px-10 py-5 bg-red-600 text-white rounded-lg text-[18px] font-semibold hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 animate-pulse"
              >
                <i className="ri-phone-line text-2xl"></i>
                (574) 329-9050
              </a>
              <a 
                href="tel:+15746221711"
                className="px-10 py-5 bg-white border-2 border-red-600 text-red-600 rounded-lg text-[18px] font-semibold hover:bg-red-600 hover:text-white transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-phone-line text-2xl"></i>
                (574) 622-1711
              </a>
              <a 
                href="mailto:bbbstanley54@gmail.com"
                className="px-8 py-5 bg-white border-2 border-red-600 text-red-600 rounded-lg text-[18px] font-semibold hover:bg-red-600 hover:text-white transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-mail-line text-2xl"></i>
                Email Us
              </a>
            </div>
            <p className="text-gray-500 text-[14px] mt-6">
              Available 24/7/365 • Average Response Time: 60 Minutes
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
